import { useApiQuery } from "@/api";
import useFeatureFlags from "@/hooks/useFeatureFlag";
import useUsers from "@/hooks/useUsers";
import {
  useHasRBACAccessToCROFeature,
  type CroFeatureAccessLevel,
} from "@/hooks/useHasRBACAccessToCROFeature";
import { useHasEngagedWithFeature } from "@/hooks/useHasEngagedWithFeature";
import type { CroFeature } from "@/types/croFeature";
import { userIsCROAdmin } from "@/util";

export type CroFeatureAccessState =
  | { state: "loading" }
  | { state: "coming-soon" }
  | { state: "no-permissions" }
  | { state: "needs-ai-consent"; userCanGrantConsent: boolean }
  | { state: "get-started" }
  | { state: "feature"; readOnly: boolean };

export function useCroFeatureAccess(
  feature: CroFeature,
): CroFeatureAccessState {
  const flag = useFeatureFlags();
  const isEarlyAccessOn = flag("freshpaintAiChat");

  const { level, loading: roleLoading } = useHasRBACAccessToCROFeature(feature);
  const { engaged, loading: engagementLoading } =
    useHasEngagedWithFeature(feature);

  const usersState = useUsers();

  const aiChatApproval = useApiQuery(["ai_chat_get_approval_status", {}], {
    enabled: feature === "ai_chat" && isEarlyAccessOn,
    staleTime: 30_000,
  });

  // Early-access is checked first so non-EA users resolve to Coming Soon
  // without waiting on any other query.
  if (!isEarlyAccessOn) {
    return { state: "coming-soon" };
  }

  if (roleLoading) {
    return { state: "loading" };
  }

  if (level === "none") {
    return { state: "no-permissions" };
  }

  if (feature === "ai_chat") {
    if (aiChatApproval.isLoading) {
      return { state: "loading" };
    }
    const approved = aiChatApproval.data?.approved ?? false;
    if (!approved) {
      return {
        state: "needs-ai-consent",
        userCanGrantConsent: userIsCROAdmin(usersState.currentUser),
      };
    }
  }

  if (engagementLoading) {
    return { state: "loading" };
  }

  if (!engaged) {
    return { state: "get-started" };
  }

  return {
    state: "feature",
    readOnly: level === "read",
  };
}

export type { CroFeatureAccessLevel };
